import "./styles.css";
import log from "loglevel";
import { RolandStructuredFile } from "../Roland-Structured-Storage/RolandStructuredFile.js";
import { RolandStructuredNamedObject } from "../Roland-Structured-Storage/RolandStructuredObject.js";
import ZenProperties from "../Roland-Structured-Storage/ZenCore/ZenProperties.js";

document.documentElement.classList.add("ui-ready");

log.setDefaultLevel("warn");

const el = {
  dropHeader: document.getElementById("dropHeader"),
  fileInput: /** @type {HTMLInputElement} */ (document.getElementById("fileInput")),
  openZenPropsBtn: document.getElementById("openZenPropsBtn"),
  tabsRow: document.getElementById("tabsRow"),
  closeTabBtn: document.getElementById("closeTabBtn"),
  aboutBtn: document.getElementById("aboutBtn"),
  aboutOverlay: document.getElementById("aboutOverlay"),
  aboutCloseBtn: document.getElementById("aboutCloseBtn"),
  aboutOkBtn: document.getElementById("aboutOkBtn"),
  statusText: document.getElementById("statusText"),
  tree: document.getElementById("tree"),
  treeMeta: document.getElementById("treeMeta"),
  details: document.getElementById("details"),
  detailsBreadcrumb: document.getElementById("detailsBreadcrumb"),
  pathInput: /** @type {HTMLInputElement} */ (document.getElementById("pathInput")),
  goPathBtn: document.getElementById("goPathBtn"),
  textSearchInput: /** @type {HTMLInputElement} */ (document.getElementById("textSearchInput")),
  textSearchBtn: document.getElementById("textSearchBtn"),
  expandAllBtn: document.getElementById("expandAllBtn"),
  collapseAllBtn: document.getElementById("collapseAllBtn"),
};

/** @typedef {"file"|"zenprops"} TabMode */
/** @typedef {{ id: string, mode: TabMode, name: string, size: number, fileType: string, fileSubType: string, root: any, arrayBuffer: ArrayBuffer|null, selectedPath?: string }} OpenTab */

/** @type {OpenTab[]} */
const tabs = [];
/** @type {string|null} */
let activeTabId = null;
let headerDragDepth = 0;

/** @type {WeakMap<HTMLElement, any>} */
const elementToNode = new WeakMap();
/** @type {WeakMap<any, HTMLElement>} */
const nodeToElement = new WeakMap();
/** @type {WeakMap<any, any[]>} */
const descriptorChildrenCache = new WeakMap();

function uid() {
  return `${Date.now().toString(16)}-${Math.random().toString(16).slice(2)}`;
}

function setStatus(text) {
  el.statusText.textContent = text;
}

function setAboutOpen(open) {
  if (!el.aboutOverlay) return;
  el.aboutOverlay.classList.toggle("hidden", !open);
}

function setTreeMeta(text) {
  el.treeMeta.textContent = text;
}

function activeTab() {
  return tabs.find((t) => t.id === activeTabId) ?? null;
}

function safeText(s) {
  return String(s ?? "");
}

function isPropertyAccessor(x) {
  return !!x && typeof x === "object" && "descriptor" in x && "rootObject" in x && typeof x.at === "function";
}

function isDescriptorNode(node) {
  return !!node && typeof node === "object" && node.__kind === "descriptor-node" && isPropertyAccessor(node.__accessor);
}

function leafValueParts(accessor, p) {
  let raw = "";
  let display = "";
  try {
    if (typeof accessor.get === "function") {
      const r = accessor.get(p.id);
      if (Array.isArray(r)) [raw, display] = r;
      else raw = r;
    } else if (p.byteLength === 1) raw = accessor.getInt8(p.id);
    else if (p.byteLength === 2) raw = accessor.getInt16(p.id);
    else if (p.byteLength === 4) raw = accessor.getInt32(p.id);
  } catch {
    raw = "";
  }
  return { raw: safeText(raw), display: safeText(display) };
}

function leafRowsTableHtml(accessor) {
  const params = Object.values(accessor?.descriptor?.parameters ?? {}).filter((p) => p && !p.block);
  if (!params.length) return "";
  const rows = params.map((p) => {
    const { raw, display } = leafValueParts(accessor, p);
    const cell = display ? `${safeText(display)} <span class="rawval">(${safeText(raw)})</span>` : safeText(raw);
    return `<tr><td>${safeText(p.description ?? p.id)}</td><td class="propid">${safeText(p.id)}</td><td>${cell}</td></tr>`;
  }).join("");
  return `<table class="blockProperties">${rows}</table>`;
}

function arrayColumnsHtml(accessor, navKey, blockParam, pathPrefix, { suppressBody = false } = {}) {
  if (suppressBody) return "";
  const count = blockParam.count ?? 1;
  const children = [];
  for (let i = 0; i < count; i++) {
    try { children.push(accessor.child(navKey, i)); } catch { /* ignore */ }
  }
  if (!children.length) return "";
  const firstDesc = blockParam.block;
  const leaves = Object.entries(firstDesc?.parameters ?? {}).filter(([, p]) => p && !p.block);
  const subBlocks = Object.entries(firstDesc?.parameters ?? {}).filter(([, p]) => p && p.block);

  let html = "";
  if (leaves.length) {
    let header = `<tr><td><b>Parameter</b></td><td style="text-align: right;"><b>ID</b></td>`;
    for (let i = 0; i < children.length; i++) header += `<td><b>[${i}]</b></td>`;
    header += `</tr>`;
    const rows = leaves.map(([, p]) => {
      let displayMeasurement = p.displayMeasurement ? ` (${safeText(p.displayMeasurement)})` : "";
      let row = `<tr><td>${safeText(p.description ?? p.id)}</td><td class="propid">${safeText(p.id)}</td>`;
      for (const child of children) {
        const { raw, display } = leafValueParts(child, p);
        const cell = display ? `${safeText(display)} <span class="rawval">(${safeText(raw)})</span>` : safeText(raw);
        row += `<td>${cell}</td>`;
      }
      return row + `</tr>`;
    }).join("");
    html += `<table class="blockProperties arrayTable">${header}${rows}</table>`;
  }
  for (const [nbKey, nb] of subBlocks) {
    for (let i = 0; i < children.length; i++) {
      html += blockSectionHtml(children[i], nbKey, nb, `${pathPrefix}[${i}]`);
    }
  }
  return html;
}

function blockSectionHtml(accessor, navKey, blockParam, pathPrefix, { suppressBody = false } = {}) {
  const count = blockParam.count ?? 1;
  const blockDesc = blockParam.block;
  const description = blockDesc?.description ?? blockDesc?.name ?? navKey;
  const myPath = pathPrefix ? `${pathPrefix}.${navKey}` : navKey;
  const headerText = count > 1
    ? `${safeText(myPath)} × ${count} → ${safeText(description)}`
    : `${safeText(myPath)} → ${safeText(description)}`;

  let body = "";
  const localSuppress = suppressBody || !!blockDesc?.uiNoRecurse;
  if (!localSuppress) {
    if (count > 1) {
      body = arrayColumnsHtml(accessor, navKey, blockParam, myPath, { suppressBody: localSuppress });
    } else {
      try {
        const child = accessor.child(navKey, 0);
        body = accessorRecursiveBodyHtml(child, myPath);
      } catch { /* ignore */ }
    }
  }
  return `<div class="properties"><div class="propertiesHeader subBlockHeader">${headerText}</div><div class="propertiesBody">${body}</div></div>`;
}

function accessorRecursiveBodyHtml(accessor, pathPrefix = "") {
  const descriptor = accessor?.descriptor;
  if (!descriptor?.parameters) return "";
  let html = leafRowsTableHtml(accessor);
  const blocks = Object.entries(descriptor.parameters).filter(([, p]) => p && p.block);
  const suppressChildren = !!descriptor?.uiNoRecurse;
  for (const [key, b] of blocks) html += blockSectionHtml(accessor, key, b, pathPrefix, { suppressBody: suppressChildren });
  return html;
}

function isRolandArrayNode(node) {
  return !!node && typeof node === "object" && Object.prototype.hasOwnProperty.call(node, "childType");
}

function nodeToAccessorPath(node) {
  if (!node) return "";
  const segments = [];
  let cur = node;
  while (cur) {
    const parent = cur.parent;
    if (!parent) break;
    const tag = parent.description?.replace(/ Chunk$/, "") ?? "";
    const idx = cur.childIndex;
    if (tag) segments.unshift(idx !== undefined ? `${tag}[${idx}]` : tag);
    cur = parent;
  }
  return segments.join(".");
}

function accessorTableHtml(accessor, title = "Accessor", { recurse = true, startPath = "" } = {}) {
  const body = recurse ? accessorRecursiveBodyHtml(accessor, startPath) : leafRowsTableHtml(accessor);
  return `<div class="properties"><div class="propertiesHeader">${safeText(title)}</div><div class="propertiesBody">${body}</div></div>`;
}

function isNumber(value) {
  return typeof value === "number" && Number.isFinite(value);
}

function hexNumber(value) {
  if (!isNumber(value)) return "";
  const sign = value < 0 ? "-" : "";
  return `${sign}0x${Math.abs(value).toString(16).toUpperCase().padStart(8, "0")}`;
}

function decimalHexNumber(value) {
  return isNumber(value) ? `${value} (${hexNumber(value)})` : "";
}

function descriptorByteLength(descriptor) {
  if (isNumber(descriptor?.byteLength)) return descriptor.byteLength;
  let max = 0;
  for (const p of Object.values(descriptor?.parameters ?? {})) {
    const blockLength = p?.blockByteLength ?? descriptorByteLength(p?.block);
    const itemLength = p?.block ? blockLength : p?.byteLength;
    const byteOffset = isNumber(p?.byteOffset) ? p.byteOffset : 0;
    const end = byteOffset + (itemLength ?? 0) * (p?.count ?? 1);
    if (end > max) max = end;
  }
  return max || undefined;
}

function rootFileForNode(node) {
  let cur = node;
  while (cur?.parent) cur = cur.parent;
  return cur ?? null;
}

function chunkForNode(node) {
  let cur = node;
  while (cur?.parent) {
    if (cur.parent === rootFileForNode(node)) return cur;
    cur = cur.parent;
  }
  return null;
}

function chunkDataStart(chunk) {
  if (!chunk || !isNumber(chunk.start)) return undefined;
  return chunk.start + (isNumber(chunk.headerSize) ? chunk.headerSize : 0);
}

function metricSet(absoluteStart, parentStart, length, chunk) {
  const dataStart = chunkDataStart(chunk);
  return [
    ["Absolute Start (from start of file)", absoluteStart],
    ["Chunk Start Relative", isNumber(absoluteStart) && isNumber(chunk?.start) ? absoluteStart - chunk.start : undefined],
    ["Chunk Data Start Relative", isNumber(absoluteStart) && isNumber(dataStart) ? absoluteStart - dataStart : undefined],
    ["Start offset from parent start", isNumber(absoluteStart) && isNumber(parentStart) ? absoluteStart - parentStart : undefined],
    ["total byte length", length],
  ];
}

function nodeMetricSet(node) {
  const chunk = chunkForNode(node);
  const metrics = metricSet(node?.start, node?.parent?.start, node?.length, chunk);
  if (isRolandArrayNode(node))
    return metrics.filter(([label]) => label !== "Chunk Data Start Relative");
  return metrics;
}

function parentDescriptorPath(path) {
  if (!path) return "";
  const parts = path.split(".");
  parts.pop();
  return parts.join(".");
}

function accessorMetricSet(accessor, parentAccessor = null, lengthOverride = undefined, offsetOverride = undefined) {
  const root = accessor?.rootObject;
  const byteOffset = isNumber(offsetOverride) ? offsetOverride : accessor?.byteOffset;
  const absoluteStart = isNumber(root?.start) && isNumber(byteOffset) ? root.start + byteOffset : undefined;
  const parentStart = parentAccessor && isNumber(root?.start) && isNumber(parentAccessor.byteOffset)
    ? root.start + parentAccessor.byteOffset
    : root?.start;
  const length = isNumber(lengthOverride) ? lengthOverride : descriptorByteLength(accessor?.descriptor);
  return metricSet(absoluteStart, parentStart, length, chunkForNode(root));
}

function descriptorNodeMetricSet(node) {
  const acc = node?.__accessor;
  const blockParam = node?.__blockParam;
  if (!isPropertyAccessor(acc)) return [];

  let parentAccessor = null;
  try {
    const parentPath = parentDescriptorPath(node.__rootPath ?? "");
    parentAccessor = parentPath ? acc.rootObject.at(parentPath) : acc.rootObject.at("");
  } catch {
    parentAccessor = null;
  }

  if (blockParam?.block && node?.__navKey) {
    const count = blockParam.count ?? 1;
    const stride = blockParam.blockByteLength ?? descriptorByteLength(blockParam.block) ?? 0;
    const offset = acc.byteOffset + (blockParam.byteOffset ?? 0);
    return accessorMetricSet(acc, acc, stride * count, offset);
  }

  return accessorMetricSet(acc, parentAccessor);
}

function detailsSummaryTableHtml(infoPairs, metrics = []) {
  const pairs = [
    ...infoPairs
      .filter((p) => p && p[0])
      .map(([label, value]) => ({ label, value: safeText(value), numeric: false })),
    ...metrics.map(([label, value]) => ({
      label,
      value: isNumber(value) ? `${value} (${hexNumber(value)})` : "",
      numeric: true,
    })),
  ];

  const rows = [];
  for (let i = 0; i < pairs.length; i += 2) {
    const left = pairs[i];
    const right = pairs[i + 1];
    rows.push(`
      <tr>
        <th>${safeText(left.label)}</th>
        <td class="${left.numeric ? "num" : ""}">${safeText(left.value)}</td>
        ${right ? `<th>${safeText(right.label)}</th><td class="${right.numeric ? "num" : ""}">${safeText(right.value)}</td>` : "<th></th><td></td>"}
      </tr>
    `);
  }

  return `
    <div class="detailsSummaryWrap">
      <table class="detailsSummary">
        <tbody>${rows.join("")}</tbody>
      </table>
    </div>
  `;
}

function isZenNode(node) {
  return !!node && typeof node === "object" && typeof node.__kind === "string" && node.__kind.startsWith("zenprops");
}

function getChildren(node) {
  if (!node) return [];
  if (isDescriptorNode(node)) {
    if (!node.__children || node.__children.length === 0) {
      node.__children = buildDescriptorChildrenForAccessor(node.__accessor, node.__rootPath ?? "");
    }
    return node.__children ?? [];
  }
  if (Array.isArray(node.children)) return node.children;
  if (isZenNode(node)) return node.__children ?? [];
  return [];
}

function descriptorNode(label, accessor, path, children = [], rootPath = "") {
  return { __kind: "descriptor-node", __label: label, __accessor: accessor, __path: path, __rootPath: rootPath, __children: children };
}

function buildDescriptorChildrenForAccessor(accessor, parentPath = "") {
  if (!isPropertyAccessor(accessor)) return [];
  const built = [];
  const blockKeys = typeof accessor.blockKeys === "function" ? accessor.blockKeys() : [];
  for (const key of blockKeys) {
    const count = typeof accessor.count === "function" ? (accessor.count(key) ?? 1) : 1;
    const myPath = parentPath ? `${parentPath}.${key}` : key;
    if (count <= 1) {
      try {
        const childAccessor = accessor.child(key, 0);
        built.push(descriptorNode(`${key}`, childAccessor, key, [], myPath));
      } catch {
        // ignore malformed descriptor entries
      }
      continue;
    }

    const indexed = [];
    for (let i = 0; i < count; i++) {
      try {
        const childAccessor = accessor.child(key, i);
        indexed.push(descriptorNode(`${key}[${i}]`, childAccessor, `${key}[${i}]`, [], `${myPath}[${i}]`));
      } catch {
        // ignore malformed descriptor entries
      }
    }
    const blockParam = accessor?.descriptor?.parameters?.[key] ?? null;
    built.push({
      ...descriptorNode(`${key} ×${count}`, accessor, key, indexed, parentPath),
      __navKey: key,
      __blockParam: blockParam,
    });
  }
  return built;
}

function buildDescriptorChildrenForObject(node) {
  if (!node || typeof node?.at !== "function" || !node?.propertiesName) return [];
  if (descriptorChildrenCache.has(node)) return descriptorChildrenCache.get(node);

  let accessor;
  try {
    accessor = node.at();
  } catch {
    descriptorChildrenCache.set(node, []);
    return [];
  }
  const built = buildDescriptorChildrenForAccessor(accessor);

  descriptorChildrenCache.set(node, built);
  return built;
}

function zenNode(kind, path, label, value, children = []) {
  return { __kind: kind, __path: path, __label: label, __value: value, __children: children };
}

function buildZenPropsRoot() {
  const keys = Object.keys(ZenProperties).sort((a, b) => a.localeCompare(b));
  const children = keys.map((k) => zenNode("zenprops-block", k, k, ZenProperties[k], []));
  const root = zenNode("zenprops-root", "ZenProperties", "ZenProperties", ZenProperties, children);
  root.__blockIndex = new Map(children.map((c) => [c.__path, c]));
  return root;
}

function zenRootFor(node) {
  let cur = node;
  while (cur && isZenNode(cur)) {
    if (cur.__kind === "zenprops-root") return cur;
    cur = cur.__parent;
  }
  return null;
}

function zenGetBlockNode(root, blockKey) {
  if (!root) return null;
  const map = root.__blockIndex;
  if (map && map.get) return map.get(blockKey) ?? null;
  return (root.__children ?? []).find((c) => c.__kind === "zenprops-block" && c.__path === blockKey) ?? null;
}

function zenAttachParents(parent, kids) {
  for (const k of kids) k.__parent = parent;
}

function ensureZenChildren(node) {
  if (!isZenNode(node)) return;
  if (node.__children && node.__children.length) return;

  if (node.__kind === "zenprops-block") {
    // Tree view policy: only show blocks in the tree (no parameters).
    node.__children = [];
    return;
  }

  node.__children = [];
}

function objectTypeName(node) {
  if (!node) return "Object";
  if (node instanceof RolandStructuredFile) {
    try {
      return node.getUint16(0, true) <= 255 ? "RolandStructuredFileHeaderA" : "RolandStructuredFileHeaderB";
    } catch {
      return "RolandStructuredFile";
    }
  }
  if (isRolandArrayNode(node)) {
    if (node.description?.startsWith("LPDa")) return "MCLPD";
    if (Array.isArray(node.appendedDataChildren)) return "RolandVariableArrayWithAppendedData";
    return "RolandStructuredVariableArray";
  }
  if (node.propertiesName)
    return node instanceof RolandStructuredNamedObject ? "NamedDescriptorBacked" : "DescriptorBacked";
  return node?.constructor?.name ?? "Object";
}

function nodeLabel(node) {
  if (isDescriptorNode(node)) return safeText(node.__label ?? node.__path ?? "Descriptor");
  if (isZenNode(node)) return safeText(node.__label ?? node.__path ?? "ZenProperties");
  const ctor = objectTypeName(node);
  const desc = node?.description ? safeText(node.description) : "";
  const idx = node?.childIndex !== undefined ? `[${safeText(node.childIndexName ?? node.childIndex)}]` : "";
  const name = typeof node?.getName === "function" ? safeText(node.getName()) : "";

  let label = ctor + idx;
  if (name) label += ` "${name}"`;
  if (desc) label += ` — ${desc}`;
  return label;
}

function nodeBadge(node) {
  if (!node) return { text: "?", cls: "" };
  if (isDescriptorNode(node)) {
    const blockName = node.__blockParam?.block?.name;
    return { text: safeText(blockName ?? node.__accessor?.descriptor?.name ?? "block"), cls: "accent" };
  }
  if (isZenNode(node)) {
    if (node.__kind === "zenprops-root") return { text: "map", cls: "green" };
    if (node.__kind === "zenprops-block") return { text: safeText(node.__path), cls: "accent" };
    return { text: "zen", cls: "" };
  }
  const hasProps = !!node?.propertiesName;
  if (hasProps) return { text: safeText(node.propertiesName), cls: "accent" };
  if (Array.isArray(node?.children) && node.children.length) return { text: "struct", cls: "green" };
  return { text: "leaf", cls: "" };
}

function clearSelectionStyles() {
  for (const tab of tabs) {
    const selected = nodeToElement.get(tab.__selectedNode);
    if (selected) selected.classList.remove("selected");
  }
}

function setSelectedNode(node) {
  const tab = activeTab();
  if (!tab) return;

  if (tab.__selectedNode && nodeToElement.get(tab.__selectedNode))
    nodeToElement.get(tab.__selectedNode).classList.remove("selected");

  tab.__selectedNode = node;
  const row = nodeToElement.get(node);
  if (row) row.classList.add("selected");

  renderDetails(node);
}

function renderDetails(nodeOrAccessor) {
  const tab = activeTab();
  if (!tab) return;

  if (isDescriptorNode(nodeOrAccessor)) {
    const dn = nodeOrAccessor;
    const acc = dn.__accessor;
    el.detailsBreadcrumb.textContent = `${tab.name} :: ${safeText(dn.__path ?? dn.__label)}`;
    const infoPairs = [
      ["File", tab.name],
      ["Kind", "Descriptor block"],
      ["Path", dn.__path ?? dn.__label ?? ""],
      ["Descriptor", acc?.descriptor?.name ?? ""],
      ["Root object", objectTypeName(acc?.rootObject)],
    ];
    const summary = detailsSummaryTableHtml(infoPairs, descriptorNodeMetricSet(dn));
    const objPath = nodeToAccessorPath(acc?.rootObject);
    const descPath = dn.__rootPath ?? "";
    const dnStartPath = descPath ? (objPath ? `${objPath}.${descPath}` : descPath) : objPath;

    // Array container nodes: render the array itself (multi-column view),
    // not the parent accessor's full block.
    const blockParam = dn.__blockParam;
    const navKey = dn.__navKey;
    const count = blockParam?.count ?? 1;
    if (blockParam?.block && navKey && count > 1) {
      const myPath = dnStartPath ? `${dnStartPath}.${navKey}` : navKey;
      const title = `${safeText(myPath)} × ${count} → ${safeText(blockParam?.block?.description ?? blockParam?.block?.name ?? navKey)}`;
      const suppress = !!acc?.descriptor?.uiNoRecurse || !!blockParam?.block?.uiNoRecurse;
      const body = suppress ? "" : arrayColumnsHtml(acc, navKey, blockParam, myPath);
      const section = `<div class="properties"><div class="propertiesHeader">${title}</div><div class="propertiesBody">${body}</div></div>`;
      el.details.innerHTML = `${summary}${section}`;
      return;
    }

    el.details.innerHTML = `${summary}${accessorTableHtml(acc, safeText(acc?.descriptor?.name ?? "Descriptor"), { startPath: dnStartPath })}`;
    return;
  }

  if (isZenNode(nodeOrAccessor)) {
    const n = nodeOrAccessor;
    const path = safeText(n.__path);
    el.detailsBreadcrumb.textContent = `${tab.name} :: ${path}`;

    const v = n.__value;
    const infoPairs = [
      ["Tab", tab.name],
      ["Kind", n.__kind],
      ["Path", path],
      ["Block name", v?.name ?? ""],
      ["byteLength", decimalHexNumber(v?.byteLength)],
      ["Parameters", v?.parameters ? Object.keys(v.parameters).length : ""],
    ];

    let blockTable = "";
    if (n.__kind === "zenprops-block" && v?.parameters) {
      const params = Object.values(v.parameters);
      const rows = params
        .map((p) => {
          const blockName = p?.block?.name ?? p?.blockName ?? "";
          const count = p?.count ?? 1;
          const byteOffset = isNumber(p?.byteOffset) ? decimalHexNumber(p.byteOffset) : safeText(p?.byteOffset ?? "");
          const itemLength = p?.block
            ? (p?.blockByteLength ?? descriptorByteLength(p?.block))
            : p?.byteLength;
          const totalLength = count > 1 && isNumber(itemLength) ? itemLength * count : undefined;
          const itemLengthText = decimalHexNumber(itemLength);
          const totalLengthText = decimalHexNumber(totalLength);
          if (p?.block) {
            const countSuffix = count !== 1 ? ` ×${count}` : "";
            const link = `<button type="button" class="zdb" data-zen-block="${safeText(blockName)}">${safeText(blockName)}${safeText(countSuffix)}</button>`;
            return `<tr><td>${safeText(p?.id ?? "")}</td><td>${safeText(p?.description ?? "")}</td><td class="num">${byteOffset}</td><td>${link}</td><td class="num">${itemLengthText}</td><td class="num">${totalLengthText}</td></tr>`;
          }
          const type = count > 1 ? `leaf ×${count}` : "leaf";
          return `<tr><td>${safeText(p?.id ?? "")}</td><td>${safeText(p?.description ?? "")}</td><td class="num">${byteOffset}</td><td>${safeText(type)}</td><td class="num">${itemLengthText}</td><td class="num">${totalLengthText}</td></tr>`;
        })
        .join("");
      blockTable = `
        <div class="properties">
          <div class="propertiesHeader">Parameters</div>
          <div class="propertiesBody">
            <table class="blockProperties zenParams">
              <tr><td><b>id</b></td><td><b>description</b></td><td><b>byteOffset</b></td><td><b>type</b></td><td><b>item length</b></td><td><b>total length</b></td></tr>
              ${rows}
            </table>
          </div>
        </div>
      `;
    }

    let rawJsonHtml = "";
    if (n.__kind === "zenprops-block") {
      let json = "";
      try {
        json = JSON.stringify(v, (k, vv) => {
          if (k === "block" && vv && typeof vv === "object")
            return vv.name ?? vv.blockName ?? "[block]";
          return vv;
        }, 4);
      } catch {
        json = safeText(v);
      }
      rawJsonHtml = `<div class="properties"><div class="propertiesHeader">Raw (JSON)</div><div class="propertiesBody"><div class="codeBox">${safeText(json)}</div></div></div>`;
    }

    el.details.innerHTML = `${detailsSummaryTableHtml(infoPairs)}${blockTable}${rawJsonHtml}`;

    if (tab.mode === "zenprops") {
      for (const btn of el.details.querySelectorAll("[data-zen-block]")) {
        btn.addEventListener("click", () => {
          const key = btn.getAttribute("data-zen-block");
          if (!key) return;
          const root = zenRootFor(n) ?? tab.root;
          const target = zenGetBlockNode(root, key);
          if (target) {
            focusNodeInTree(target);
            setStatus(`Jumped to block: ${key}`);
          }
        });
      }
    }
    return;
  }

  const isAccessor = isPropertyAccessor(nodeOrAccessor);
  const node = isAccessor ? nodeOrAccessor.rootObject : nodeOrAccessor;

  const breadcrumb = (() => {
    if (isAccessor) return `${node?.getFullName?.() ?? nodeLabel(node)} :: ${nodeOrAccessor.descriptor?.name ?? "PropertyAccessor"}`;
    return node?.getFullName?.() ?? nodeLabel(node);
  })();
  el.detailsBreadcrumb.textContent = safeText(breadcrumb);

  const infoPairs = [
    ["File", tab.name],
    ["Type", `${tab.fileType}${tab.fileSubType ? ` / ${tab.fileSubType}` : ""}`],
    ["Class", objectTypeName(node)],
    ["Length", node?.length ?? ""],
    ["Children", Array.isArray(node?.children) ? node.children.length : ""],
    ["propertiesName", node?.propertiesName ?? ""],
  ];

  let propertiesHtml = "";
  try {
    const nodeStartPath = nodeToAccessorPath(node);
    if (isAccessor) {
      const d = nodeOrAccessor.descriptor;
      propertiesHtml = accessorTableHtml(nodeOrAccessor, safeText(d?.name ?? "Accessor"), { startPath: nodeStartPath });
    } else if (node?.propertiesName && typeof node?.at === "function") {
      // `RolandStructuredObject.at()` can be called with an empty path to get
      // the root accessor. `RolandStructuredFile.at()` requires a real path
      // (first segment is a chunk tag), so guard this call.
      let accessor = null;
      try {
        accessor = node.at("");
      } catch {
        accessor = null;
      }
      if (isPropertyAccessor(accessor))
        propertiesHtml = accessorTableHtml(accessor, safeText(node.propertiesName), { recurse: !isRolandArrayNode(node), startPath: nodeStartPath });
    }

    if (!propertiesHtml) {
      propertiesHtml = `<div class="emptyState"><div class="emptyTitle">No ZenProperties block</div><div class="emptyBody">This node doesn’t expose a ZenProperties descriptor (try selecting a child with a properties badge).</div></div>`;
    }
  } catch (e) {
    propertiesHtml = `<div class="errorBox">${safeText(e?.stack ?? e)}</div>`;
  }

  const metrics = isAccessor ? accessorMetricSet(nodeOrAccessor) : nodeMetricSet(node);
  el.details.innerHTML = `${detailsSummaryTableHtml(infoPairs, metrics)}${propertiesHtml}`;
}

/**
 * Row UI for the tree. Must not use a focusable control inside `<summary>` or
 * the browser will not toggle `<details>` (cannot expand past the first level).
 * @param {"summary"|"div"} rowTag
 */
function makeNodeRow(node, rowTag, options = {}) {
  const { hasChildren = false, isOpen = false, onToggle = null } = options;
  const row = document.createElement(rowTag);
  row.className = rowTag === "summary" ? "nodeRowSummary" : "nodeRowLeaf";

  const badge = nodeBadge(node);
  const expander = hasChildren
    ? `<button type="button" class="treeExpander" aria-label="Toggle children">${isOpen ? "-" : "+"}</button>`
    : `<button type="button" class="treeExpander treeExpanderSpacer" tabindex="-1" aria-hidden="true">+</button>`;
  row.innerHTML = `
    <span class="nodeRowContent">
      <span class="treeExpanderSlot">${expander}</span>
      <span class="badge ${badge.cls}">${safeText(badge.text)}</span>
      <span class="nodeLabel">${safeText(nodeLabel(node))}</span>
      <span class="nodeSub">${(isZenNode(node) || isDescriptorNode(node)) ? "" : `${safeText(node?.start ?? "")}:${safeText(node?.length ?? "")}`}</span>
    </span>
  `;

  elementToNode.set(row, node);
  nodeToElement.set(node, row);

  row.addEventListener("click", (ev) => {
    if (rowTag === "summary") ev.preventDefault();
    const target = /** @type {HTMLElement} */ (ev.target);
    if (target?.closest(".treeExpander")) return;
    setSelectedNode(node);
  });

  if (hasChildren && typeof onToggle === "function") {
    const expanderBtn = row.querySelector(".treeExpander");
    expanderBtn?.addEventListener("click", (ev) => {
      ev.preventDefault();
      ev.stopPropagation();
      const nextOpen = !!onToggle();
      expanderBtn.textContent = nextOpen ? "−" : "+";
    });
  }

  return row;
}

function buildTreeForNode(node, depth = 0) {
  const wrapper = document.createElement("div");
  wrapper.className = "node";
  wrapper.style.paddingLeft = `${Math.min(depth, 14) * 6}px`;

  ensureZenChildren(node);
  let kidsList = getChildren(node);
  if (!isZenNode(node) && !isDescriptorNode(node)) {
    const descriptorKids = buildDescriptorChildrenForObject(node);
    if (descriptorKids.length) kidsList = [...kidsList, ...descriptorKids];
  }
  const hasChildren = Array.isArray(kidsList) && kidsList.length > 0;
  const details = document.createElement("details");
  details.className = hasChildren ? "treeNode" : "treeNode leafNode";
  details.open = hasChildren ? depth < 1 : false;
  const summaryRow = makeNodeRow(node, "summary", {
    hasChildren,
    isOpen: details.open,
    onToggle: hasChildren
      ? () => {
          details.open = !details.open;
          return details.open;
        }
      : null,
  });
  details.appendChild(summaryRow);

  if (hasChildren) {
    const expanderBtn = summaryRow.querySelector(".treeExpander");
    const kids = document.createElement("div");
    let childrenRendered = false;

    const ensureChildrenRendered = () => {
      if (childrenRendered) return;
      for (const child of kidsList) kids.appendChild(buildTreeForNode(child, depth + 1));
      childrenRendered = true;
    };

    const syncExpander = (isOpen) => {
      if (expanderBtn) expanderBtn.textContent = isOpen ? "-" : "+";
    };

    if (details.open) ensureChildrenRendered();
    syncExpander(details.open);
    details.addEventListener("toggle", () => {
      if (details.open) ensureChildrenRendered();
      syncExpander(details.open);
    });
    details.appendChild(kids);
  }

  wrapper.appendChild(details);
  return wrapper;
}

function renderTree() {
  const tab = activeTab();
  el.tree.innerHTML = "";

  if (!tab) {
    setTreeMeta("");
    el.detailsBreadcrumb.textContent = "";
    el.details.innerHTML = `<div class="emptyState"><div class="emptyTitle">No file</div><div class="emptyBody">Open a file to explore it.</div></div>`;
    return;
  }

  setTreeMeta(tab.mode === "zenprops" ? `${Object.keys(ZenProperties).length.toLocaleString()} blocks` : `${tab.size.toLocaleString()} bytes`);
  el.tree.appendChild(buildTreeForNode(tab.root, 0));
  setStatus(tab.mode === "zenprops" ? `Loaded ${tab.name}. Click a block/parameter to inspect it.` : `Loaded ${tab.name}. Click a node to view details.`);

  // Default select root.
  setSelectedNode(tab.root);
}

function renderTabs() {
  el.tabsRow.innerHTML = "";
  for (const t of tabs) {
    const btn = document.createElement("button");
    btn.type = "button";
    btn.className = `tab ${t.id === activeTabId ? "active" : ""}`;
    btn.innerHTML = `
      <span class="tabName">${safeText(t.name)}</span>
      <span class="tabMeta">${safeText(t.fileType)}</span>
      <span class="tabClose" title="Close">×</span>
    `;

    btn.addEventListener("click", (ev) => {
      const target = /** @type {HTMLElement} */ (ev.target);
      if (target && target.classList.contains("tabClose")) {
        ev.preventDefault();
        closeTab(t.id);
        return;
      }
      setActiveTab(t.id);
    });

    el.tabsRow.appendChild(btn);
  }
}

function setActiveTab(id) {
  activeTabId = id;
  renderTabs();
  renderTree();
}

function closeTab(id) {
  const idx = tabs.findIndex((t) => t.id === id);
  if (idx < 0) return;
  tabs.splice(idx, 1);
  if (activeTabId === id) activeTabId = tabs[idx]?.id ?? tabs[idx - 1]?.id ?? tabs[0]?.id ?? null;
  renderTabs();
  renderTree();
}

function expandCollapseAll(open) {
  const tab = activeTab();
  if (!tab) return;
  for (const d of el.tree.querySelectorAll("details")) d.open = open;
}

function focusNodeInTree(node) {
  const elRow = nodeToElement.get(node);
  if (!elRow) return false;
  // Open ancestors
  let cur = elRow.parentElement;
  while (cur) {
    if (cur.tagName.toLowerCase() === "details") cur.open = true;
    cur = cur.parentElement;
  }
  elRow.scrollIntoView({ block: "center" });
  setSelectedNode(node);
  return true;
}

function searchTreeByText(query) {
  const tab = activeTab();
  if (!tab) return;
  const q = query.trim().toLowerCase();
  if (!q) return;

  /** @type {any[]} */
  const matches = [];

  const walk = (n) => {
    const label = nodeLabel(n).toLowerCase();
    const full = (typeof n?.getFullName === "function" ? n.getFullName() : "").toLowerCase();
    if (label.includes(q) || full.includes(q)) matches.push(n);
    ensureZenChildren(n);
    for (const c of getChildren(n)) walk(c);
  };
  walk(tab.root);

  if (!matches.length) {
    setStatus(`No matches for "${query}".`);
    return;
  }
  setStatus(`Found ${matches.length} match(es) for "${query}". Showing first.`);
  focusNodeInTree(matches[0]);
}

function goPath(path) {
  const tab = activeTab();
  if (!tab) return;
  const p = path.trim();
  if (!p) return;

  if (tab.mode === "zenprops") {
    try {
      const segs = p.split(".").filter(Boolean);
      const blockKey = segs[0];
      const block = ZenProperties[blockKey];
      if (!block) throw new Error(`No ZenProperties entry "${blockKey}"`);

      const root = tab.root;
      const blockNode = root.__children.find((c) => c.__path === blockKey);
      if (!blockNode) throw new Error("Internal: block node not found");
      ensureZenChildren(blockNode);

      if (segs.length === 1) {
        focusNodeInTree(blockNode);
        setStatus(`ZenProperties OK: ${p}`);
        return;
      }
      throw new Error(`Tree only shows blocks. Use a block key like "${blockKey}" and select parameters on the right.`);
    } catch (e) {
      setStatus(`ZenProperties path error: ${p}`);
      el.detailsBreadcrumb.textContent = `${tab.name} :: path error`;
      el.details.innerHTML = `<div class="errorBox">${safeText(e?.stack ?? e)}</div>`;
      return;
    }
  }

  try {
    const result = tab.root.at ? tab.root.at(p) : tab.root?.at?.(p);
    if (!result) throw new Error(`No result for path "${p}"`);
    if (result?.rootObject) {
      // PropertyAccessor: select its root and show accessor details.
      focusNodeInTree(result.rootObject);
      renderDetails(result);
    } else {
      focusNodeInTree(result);
    }
    setStatus(`Path OK: ${p}`);
  } catch (e) {
    setStatus(`Path error: ${p}`);
    el.detailsBreadcrumb.textContent = `${tab.name} :: path error`;
    el.details.innerHTML = `<div class="errorBox">${safeText(e?.stack ?? e)}</div>`;
  }
}

async function openFiles(fileList) {
  const files = [...fileList];
  for (const f of files) {
    const arrayBuffer = await f.arrayBuffer();
    let root;
    try {
      root = RolandStructuredFile.createFromBuffer(arrayBuffer, f.name);
    } catch (e) {
      const id = uid();
      tabs.push({
        id,
        mode: "file",
        name: f.name,
        size: f.size,
        fileType: "parseError",
        fileSubType: "",
        root: { description: "Parse error", children: [], at() { throw e; } },
        arrayBuffer,
      });
      activeTabId = id;
      renderTabs();
      renderTree();
      el.details.innerHTML = `<div class="errorBox">${safeText(e?.stack ?? e)}</div>`;
      continue;
    }

    const id = uid();
    tabs.push({
      id,
      mode: "file",
      name: f.name,
      size: f.size,
      fileType: root.fileType ?? "unknown",
      fileSubType: root.fileSubType ?? "",
      root,
      arrayBuffer,
    });
    activeTabId = id;
  }
  renderTabs();
  renderTree();
}

function setHeaderDragOver(isDragOver) {
  if (!el.dropHeader) return;
  el.dropHeader.classList.toggle("dragOver", isDragOver);
}

function openZenPropertiesTab() {
  const id = uid();
  tabs.push({
    id,
    mode: "zenprops",
    name: "ZenProperties",
    size: 0,
    fileType: "schema",
    fileSubType: "",
    root: buildZenPropsRoot(),
    arrayBuffer: null,
  });
  activeTabId = id;
  renderTabs();
  renderTree();
}

// Dev-only bridge for `autoload-dev.js` (which is only bundled in dev).
// In production builds this is harmless: nothing calls it.
globalThis.__rssUiOpenFiles = openFiles;
globalThis.__rssUiSetStatus = setStatus;

// Wire up UI
el.dropHeader?.addEventListener("dragenter", (ev) => {
  ev.preventDefault();
  headerDragDepth += 1;
  setHeaderDragOver(true);
});

el.dropHeader?.addEventListener("dragover", (ev) => {
  ev.preventDefault();
});

el.dropHeader?.addEventListener("dragleave", (ev) => {
  ev.preventDefault();
  headerDragDepth = Math.max(0, headerDragDepth - 1);
  if (headerDragDepth === 0) setHeaderDragOver(false);
});

el.dropHeader?.addEventListener("drop", async (ev) => {
  ev.preventDefault();
  headerDragDepth = 0;
  setHeaderDragOver(false);
  const files = ev.dataTransfer?.files;
  if (!files || files.length === 0) return;
  await openFiles(files);
});

el.fileInput.addEventListener("change", async () => {
  if (!el.fileInput.files || !el.fileInput.files.length) return;
  await openFiles(el.fileInput.files);
  el.fileInput.value = "";
});

el.openZenPropsBtn.addEventListener("click", () => openZenPropertiesTab());

el.aboutBtn?.addEventListener("click", () => setAboutOpen(true));
el.aboutCloseBtn?.addEventListener("click", () => setAboutOpen(false));
el.aboutOkBtn?.addEventListener("click", () => setAboutOpen(false));
el.aboutOverlay?.addEventListener("mousedown", (ev) => {
  // Click outside modal closes.
  if (ev.target === el.aboutOverlay) setAboutOpen(false);
});
document.addEventListener("keydown", (ev) => {
  if (ev.key === "Escape") setAboutOpen(false);
});

el.closeTabBtn.addEventListener("click", () => {
  if (!activeTabId) return;
  closeTab(activeTabId);
});

el.expandAllBtn.addEventListener("click", () => expandCollapseAll(true));
el.collapseAllBtn.addEventListener("click", () => expandCollapseAll(false));

el.textSearchBtn.addEventListener("click", () => searchTreeByText(el.textSearchInput.value));
el.textSearchInput.addEventListener("keydown", (ev) => {
  if (ev.key === "Enter") searchTreeByText(el.textSearchInput.value);
});

el.goPathBtn.addEventListener("click", () => goPath(el.pathInput.value));
el.pathInput.addEventListener("keydown", (ev) => {
  if (ev.key === "Enter") goPath(el.pathInput.value);
});

setStatus("Open one or more files to begin.");

